import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faDownload } from '@fortawesome/free-solid-svg-icons';

//STYLES
import { CardListar, ContainerLista } from './styled';

//APP
import { useFormOsContext } from '../../../../context/FormOsContext';
import { motivoSemPermissao } from '../../../../../../../../meta/usePermissoesOs';
import { calcularPorcentagem } from '../../meta';
import { haPorPonto, calcularAmostraHa } from './meta';
import type { AnaliseAdicionada, GeoPointFeature } from '../../../../types';

//COMPONENTS
import ButtonCustom from '../../../../../../../../../../../../Components/ButtonCustom';
import { LineDivider } from '../../../../../../../../../../../../Components/CustomUi/LineDivider';
import { DeleteButton } from '../../../../../../../../../../../../Components/CustomUi/deleteButton';
import { PontosAnalises } from '../pontos';
import { ModalCopiarAnalises } from '../modalCopiar';
import { BaixarArquivoAnalise } from './components/baixarArquivo';
import { Popover, Tooltip } from 'antd';
import { Render } from '../../../../../../../../../../../../Components/CustomUi/render';

/** Lista de análises adicionadas (cards). Consome estado via context. */
export function ListaAnalises() {
    const {
        area_propriedade,
        campos,
        analises: {
            analises,
            grupoAnalises,
            pontosTotalMalha,
            talhaoArea,
            setAnalises,
            deletarAnalise,
            removerPonto,
            inserirPontosSelecionados,
        },
        permissoes,
    } = useFormOsContext()

    const [abrirModalCopia, setAbrirModalCopia] = useState(false)
    const [pontos_copia, setPontos_copia] = useState<GeoPointFeature[]>([])
    const [confirmar_exclusao, setConfirmar_exclusao] = useState<boolean | number>(false)
    const [analise_atual, setAnalise_atual] = useState<AnaliseAdicionada | null>(null)
    /** id da análise com o popover de download aberto (um por vez). */
    const [open_download, setOpen_download] = useState<AnaliseAdicionada['id'] | null>(null)

    /** ha para cada amostra (área da propriedade / área de amostra do laudo). */
    const amostraHa = calcularAmostraHa(area_propriedade, campos.area_amostra_laudo)

    function copiar(item: AnaliseAdicionada) {
        setPontos_copia(item?.pontos_selecionados)
        setAbrirModalCopia(true)
    }

    function deletar(id: AnaliseAdicionada['id']) {
        deletarAnalise(id)
        setConfirmar_exclusao(false)
        setAnalise_atual(null)
    }

    return (
        <ContainerLista>
            {analises.map((item, index) => (
                <CardListar key={item?.id ?? index}>
                    <div className='card__header'>
                        <div className='header__infos'>
                            <Tooltip title="Nome da análise">
                                <div style={{ backgroundColor: item?.cor }}>{item?.analise_nome}</div>
                            </Tooltip>
                            <Tooltip title="Profundidade">
                                <p style={{ color: item?.cor }}>{item?.profundidade}</p>
                            </Tooltip>
                        </div>
                        <div className='header__infos__buttons'>
                            {/* Sem permissão fica desabilitado, não oculto — o tooltip diz o motivo. */}
                            <Tooltip title={motivoSemPermissao(permissoes?.inclusao !== 0, 'clonar análise') ?? "Clonar análise"}><samp>
                                <ButtonCustom
                                    type='secondary'
                                    onClick={() => copiar(item)}
                                    icon={<FontAwesomeIcon icon={faCopy} />}
                                    disabled={permissoes?.inclusao === 0}
                                />
                            </samp></Tooltip>
                            <Popover
                                trigger='click'
                                open={open_download === item?.id}
                                onOpenChange={(aberto: boolean) => setOpen_download(aberto ? item?.id : null)}
                                content={() => (
                                    <BaixarArquivoAnalise
                                        analise={item}
                                        nomePropriedade={campos.propriedade}
                                        nomeTalhao={campos.talhao}
                                        aoBaixar={() => setOpen_download(null)}
                                    />
                                )}
                            >
                                {/* span nativo como gatilho: o Popover injeta o onClick (com evento)
                                nele; o ButtonCustom fica só visual (não engole o evento). */}
                                <Tooltip title="Baixar KML ou Shape" placement='bottom'><samp>
                                    <span style={{ display: 'inline-flex' }}>
                                        <ButtonCustom
                                            type='primary'
                                            onClick={undefined}
                                            icon={<FontAwesomeIcon icon={faDownload} />}
                                        />
                                    </span>
                                </samp></Tooltip>
                            </Popover>
                            <Tooltip placement='bottom' title={motivoSemPermissao(permissoes?.exclusao !== 0, 'excluir análise') ?? "Excluir análise"}><samp>
                                <DeleteButton
                                    disabledTrigger={permissoes?.exclusao === 0}
                                    open={confirmar_exclusao === true && analise_atual?.id === item?.id}
                                    trigger={setConfirmar_exclusao}
                                    onTrigger={() => setAnalise_atual(item)}
                                    loading={false}
                                    title='Tem certeza que deseja excluir esta análise?'
                                    message={`A análise ${item?.analise_nome} (${item?.profundidade}) e seus pontos selecionados serão removidos.`}
                                    deleteds={[item?.analise_nome, item?.profundidade]}
                                    onDelete={() => deletar(item?.id)}
                                    typeButtonTrigger='primary'
                                />
                            </samp></Tooltip>
                        </div>
                    </div>

                    <LineDivider />

                    <PontosAnalises
                        initialPoints={item?.pontos_selecionados}
                        inserirPontosSelecionados={inserirPontosSelecionados}
                        id={item?.id}
                        cod_analise={item?.cod_analise}
                        removerPonto={removerPonto}
                        podeRemover={permissoes?.exclusao !== 0}
                    />

                    <LineDivider />

                    <div className='detalhes__analise'>
                        <h3>Detalhes</h3>
                        <div className='info__detalhes'>
                            <div className='detalhes' data-active={true}>
                                <span>Valor</span>
                                <strong>{item?.valor_analise}</strong>
                            </div>
                            <div className='detalhes'>
                                <span>Amostras</span>
                                <strong>
                                    {`${item?.pontos_selecionados?.length} de ${pontosTotalMalha} ${calcularPorcentagem(
                                        item?.pontos_selecionados?.length,
                                        pontosTotalMalha
                                    )}`}
                                </strong>
                            </div>
                            <div className='detalhes'>
                                <span>Grid amostral</span>
                                <strong>{haPorPonto(talhaoArea, item?.pontos_selecionados)} ha/ponto</strong>
                            </div>
                        </div>
                        <div className='detalhes__observacao'>
                            <span>Observação</span>
                            <p>{amostraHa} ha para cada amostra</p>
                        </div>
                    </div>
                </CardListar>
            ))}

            <ModalCopiarAnalises
                abrirModalCopia={abrirModalCopia}
                setAbrirModalCopia={setAbrirModalCopia}
                pontos_selecionados={pontos_copia}
                grupoAnalises={grupoAnalises}
                analises={analises}
                setAnalises={setAnalises}
            />
        </ContainerLista>
    )
}
